import type {
  DailyRevenue,
  DeliveryPlatform,
  ExpenseCategory,
  ExpenseEntry,
  Ingredient,
  Investment,
  InventoryCount,
  MenuItem,
  Recipe,
  Target,
  TenantSettings,
} from './types';
import { valueInventoryLines } from './inventory';
import { addMonths } from './period';

/**
 * Datos de demo de un restaurante ficticio ("Trattoria Marina", 45 plazas),
 * con la misma estructura que las plantillas de Alex. Sirven para abrir la
 * app sin cargar nada y como fixture de los tests.
 */

const T = 'demo';

/* ------------------------------------------------------------------ */
/* Cuando pasa la demo                                                 */
/* ------------------------------------------------------------------ */

/**
 * La demo se mueve sola en el tiempo.
 *
 * Antes estaba clavada a febrero-julio de 2026: el dia que se escribio era
 * "ahora mismo", y cada mes que pasa envejece. Alex no quiere tener que
 * alimentar una cuenta demo eternamente para que parezca actual (2 sep 2026),
 * asi que las fechas se calculan al cargar.
 *
 * Cubre doce meses cerrados MAS el mes en curso, entero. Dos peticiones de
 * Alex del 4 de septiembre de 2026:
 *
 * - La app arranca en «Mes en curso» (mejora 34). Si ese mes no estuviera en
 *   la demo, la primera pantalla saldria vacia. Por eso el mes en curso va
 *   completo, tambien los dias que aun no han llegado: es lo mismo que hizo
 *   el con su propia cuenta, cargar septiembre entero el dia 4. La decision
 *   anterior (solo meses cerrados, 2 sep) queda sustituida por esta.
 * - Que haya datos en todos los periodos que ofrece el selector (mejora 35):
 *   «Últimos 12 meses» y «Año en curso» tienen que enseñar un restaurante
 *   que lleva todo el año con Alexia, no seis meses y luego nada.
 *
 * Los meses no son calcas unos de otros: hay una estacionalidad por mes del
 * año y un ruido determinista por mes y por dia. Determinista a proposito:
 * la demo tiene que dar los mismos numeros en cada recarga y en los tests.
 */
const MESES_CERRADOS_DEMO = 12;
const MESES_DEMO = MESES_CERRADOS_DEMO + 1;

function mesDeHoy(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Ultimo dia del mes, "31". */
function lastDay(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0');
}

const ULTIMO_MES = mesDeHoy();
const START_MONTH = addMonths(ULTIMO_MES, -(MESES_DEMO - 1));
export const DEMO_MONTHS = Array.from({ length: MESES_DEMO }, (_, i) => addMonths(START_MONTH, i));
/** El ultimo mes de la demo: el que esta en curso. */
const MES_FINAL = DEMO_MONTHS[DEMO_MONTHS.length - 1];

/**
 * Ruido determinista en [-1, 1] a partir de un texto (una fecha, un mes).
 * Un hash sencillo (FNV-1a) basta: no busca ser aleatorio, busca que dos
 * dias distintos no se parezcan y que el mismo dia de siempre el mismo.
 */
function ruido(texto: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < texto.length; i++) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return ((h % 20_001) / 10_000) - 1;
}

/**
 * Estacionalidad por mes del ano de un restaurante de ciudad con terraza:
 * enero flojo, verano y diciembre fuertes. Sobre ella, un ±4 % por mes para
 * que dos meses de la misma estacion no salgan identicos.
 */
const ESTACIONALIDAD_ANUAL = [0.84, 0.86, 0.92, 0.98, 1.05, 1.1, 1.18, 1.22, 1.08, 1.0, 0.92, 1.12];

function estacionalidad(month: string): number {
  const mes = Number(month.slice(5, 7)) - 1;
  return ESTACIONALIDAD_ANUAL[mes] * (1 + 0.04 * ruido(`mes:${month}`));
}

const now = new Date().toISOString();

/**
 * Un dia concreto de uno de los meses de la demo: `diaDe(-1, '18')` es el 18
 * del penultimo mes. Sirve para colgar facturas y compras de fechas que se
 * mueven con el resto.
 */
function diaDe(desdeElFinal: number, dia: string): string {
  return `${DEMO_MONTHS[DEMO_MONTHS.length - 1 + desdeElFinal]}-${dia}`;
}

/* ------------------------------------------------------------------ */
/* Cuanto cuesta todo                                                  */
/* ------------------------------------------------------------------ */

/**
 * La demo es un restaurante mexicano y factura en pesos (peticion de Alex,
 * 2 sep 2026). Los importes nacieron en euros, asi que van multiplicados por
 * un factor unico.
 *
 * Que sea UNO SOLO es lo importante: multiplicar todo por lo mismo no mueve
 * ni un decimal de los porcentajes —food cost, margenes, peso de cada gasto
 * sobre ventas—, que es de lo que va la demo. Solo cambia la escala para que
 * un solomillo a 490 pesos se lea como un solomillo y no como una ganga.
 */
const MXN = 20;

/** Importe en pesos, con dos decimales. */
const p = (euros: number): number => Math.round(euros * MXN * 100) / 100;

/** Precio de carta en pesos, redondeado a 5: nadie pone 597,60 en una carta. */
const pvp = (euros: number): number => Math.round((euros * MXN) / 5) * 5;

/**
 * IVA mexicano. En Mexico el consumo en restaurante y casi todo lo que compra
 * uno va al 16 %; los tipos espanyoles (10 % reducido, 21 % general) no
 * existen alli. Un negocio mexicano con IVA del 21 % en el alquiler es una
 * demo que se contradice a si misma en la pantalla de Impuestos.
 */
const IVA = 16;

export const demoPlatforms: DeliveryPlatform[] = [
  { platformId: 'plt-uber', name: 'Uber Eats', commissionPct: 30, active: true },
  { platformId: 'plt-glovo', name: 'Glovo', commissionPct: 25, active: true },
  { platformId: 'plt-justeat', name: 'Just Eat', commissionPct: 27, active: false },
];

export const demoSettings: TenantSettings = {
  vatPct: IVA,
  bankCommissionPct: 2.1,
  platforms: demoPlatforms,
  // Subgrupos de ejemplo (peticion de Alex: los suyos, dentro de sus 3 grupos)
  expenseSubgroups: {
    materia_prima: ['Carnes y pescados', 'Bodega', 'Frutas y verduras'],
    personal: ['Cocina', 'Sala'],
    generales: ['Local', 'Marketing'],
  },
  alerts: { costIncreasePct: 5, costDecreasePct: 10, minGrossMarginPct: 65 },
};

/* ------------------------------------------------------------------ */
/* Ingredientes                                                        */
/* ------------------------------------------------------------------ */

const ing = (
  ingredientId: string,
  name: string,
  brand: string,
  presentation: string,
  purchaseQty: number,
  purchaseUnit: Ingredient['purchaseUnit'],
  netPrice: number,
  group: Ingredient['group'],
  extra: Partial<Ingredient> = {},
): Ingredient => ({
  tenantId: T,
  ingredientId,
  name,
  brand,
  presentation,
  group,
  purchaseQty,
  purchaseUnit,
  netPrice,
  vatPct: IVA,
  active: true,
  updatedAt: now,
  ...extra,
});

export const demoIngredients: Ingredient[] = [
  ing('ing-albahaca', 'Albahaca fresca', 'Granel', 'kg', 1, 'kg', p(17.7), 'frutas_verduras'),
  ing('ing-aove', 'Aceite de oliva virgen extra', 'Oro Bailén', 'botella', 1, 'l', p(20.15), 'aceites', { densityGPerMl: 0.916 }),
  ing('ing-parmesano', 'Queso parmesano', 'Zanetti', 'pieza', 1, 'kg', p(21.5), 'lacteos_huevos'),
  ing('ing-pinones', 'Piñones', 'DelBosque Select', 'bolsa', 0.5, 'kg', p(28.0), 'secos_granos'),
  ing('ing-ajo', 'Ajo cabeza', 'Granel', 'kg', 1, 'kg', p(18.7), 'frutas_verduras'),
  ing('ing-sal', 'Sal fina', 'La Fina', 'saco', 5, 'kg', p(1.4), 'abarrotes'),
  ing('ing-solomillo', 'Solomillo de ternera', 'Carnes Roma', 'pieza', 5, 'kg', p(92.5), 'carnes_pescados'),
  ing('ing-pulpo', 'Pulpo cocido', 'Distmar', 'bolsa', 3, 'kg', p(68.4), 'carnes_pescados'),
  ing('ing-patata', 'Patata agria', 'Fruza', 'saco', 25, 'kg', p(18.75), 'frutas_verduras'),
  ing('ing-tomate', 'Tomate pera', 'Frutas Eloy', 'caja', 10, 'kg', p(14.5), 'frutas_verduras'),
  ing('ing-cebolla', 'Cebolla', 'Granel', 'saco', 10, 'kg', p(9.2), 'frutas_verduras'),
  ing('ing-harina', 'Harina de trigo', 'Molino Real', 'saco', 25, 'kg', p(16.25), 'secos_granos'),
  ing('ing-nata', 'Nata 35% MG', 'Central Lechera', 'brik', 1, 'l', p(3.4), 'lacteos_huevos', { densityGPerMl: 1.02 }),
  ing('ing-mantequilla', 'Mantequilla', 'Arias', 'pastilla', 1, 'kg', p(8.6), 'lacteos_huevos'),
  ing('ing-pimenton', 'Pimentón de la Vera', 'Especia Fina', 'bolsa', 0.5, 'kg', p(8.9), 'abarrotes'),
  ing('ing-envase', 'Envase delivery', 'Choice', 'caja', 100, 'pz', p(24.0), 'desechables'),
  ing('ing-bolsa', 'Bolsa de papel delivery', 'Choice', 'paquete', 250, 'pz', p(18.0), 'desechables'),
];

/* ------------------------------------------------------------------ */
/* Recetas y subrecetas                                                */
/* ------------------------------------------------------------------ */

export const demoRecipes: Recipe[] = [
  {
    tenantId: T,
    recipeId: 'rec-pesto',
    name: 'Pesto genovés',
    kind: 'subreceta',
    code: 'SUB001',
    group: 'Subrecetas',
    components: [
      { ref: { kind: 'ingredient', ingredientId: 'ing-albahaca' }, block: 'ingrediente', qty: 195, unit: 'g', wastePct: 35 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-aove' }, block: 'ingrediente', qty: 400, unit: 'ml', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-parmesano' }, block: 'ingrediente', qty: 150, unit: 'g', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-pinones' }, block: 'ingrediente', qty: 100, unit: 'g', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-ajo' }, block: 'ingrediente', qty: 27, unit: 'g', wastePct: 10 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-sal' }, block: 'ingrediente', qty: 10, unit: 'g', wastePct: 0 },
    ],
    yieldQty: 1,
    yieldUnit: 'l',
    portions: 1,
    safetyMarginPct: 10,
    useYieldCostAsBase: true,
    lastCostedAt: '2026-05-28',
    active: true,
    updatedAt: now,
  },
  {
    tenantId: T,
    recipeId: 'rec-sofrito',
    name: 'Sofrito base',
    kind: 'subreceta',
    code: 'SUB002',
    group: 'Subrecetas',
    components: [
      { ref: { kind: 'ingredient', ingredientId: 'ing-cebolla' }, block: 'ingrediente', qty: 1.5, unit: 'kg', wastePct: 15 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-tomate' }, block: 'ingrediente', qty: 1, unit: 'kg', wastePct: 10 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-ajo' }, block: 'ingrediente', qty: 60, unit: 'g', wastePct: 10 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-aove' }, block: 'ingrediente', qty: 200, unit: 'ml', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-sal' }, block: 'ingrediente', qty: 15, unit: 'g', wastePct: 0 },
    ],
    yieldQty: 2,
    yieldUnit: 'kg',
    portions: 1,
    safetyMarginPct: 10,
    useYieldCostAsBase: true,
    lastCostedAt: '2026-05-28',
    active: true,
    updatedAt: now,
  },
  {
    tenantId: T,
    recipeId: 'rec-solomillo',
    name: 'Solomillo con pesto y patata',
    kind: 'plato',
    code: 'PRI001',
    group: 'Principales',
    // El pesto aporta lacteos (parmesano) y frutos secos (piñones)
    allergens: ['lacteos', 'frutos_secos'],
    components: [
      { ref: { kind: 'ingredient', ingredientId: 'ing-solomillo' }, block: 'ingrediente', qty: 200, unit: 'g', wastePct: 18 },
      { ref: { kind: 'recipe', recipeId: 'rec-pesto' }, block: 'ingrediente', qty: 40, unit: 'ml', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-patata' }, block: 'ingrediente', qty: 150, unit: 'g', wastePct: 22 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-aove' }, block: 'ingrediente', qty: 20, unit: 'ml', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-sal' }, block: 'ingrediente', qty: 4, unit: 'g', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-envase' }, block: 'empaque', qty: 1, unit: 'pz', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-bolsa' }, block: 'empaque', qty: 1, unit: 'pz', wastePct: 0 },
    ],
    yieldQty: 1,
    yieldUnit: 'pz',
    portions: 1,
    safetyMarginPct: 10,
    useYieldCostAsBase: false,
    lastCostedAt: '2026-05-20',
    active: true,
    updatedAt: now,
  },
  {
    tenantId: T,
    recipeId: 'rec-pulpo',
    name: 'Pulpo a la gallega',
    kind: 'plato',
    code: 'ENT001',
    group: 'Entrantes',
    allergens: ['moluscos'],
    components: [
      { ref: { kind: 'ingredient', ingredientId: 'ing-pulpo' }, block: 'ingrediente', qty: 180, unit: 'g', wastePct: 5 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-patata' }, block: 'ingrediente', qty: 120, unit: 'g', wastePct: 22 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-aove' }, block: 'ingrediente', qty: 25, unit: 'ml', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-pimenton' }, block: 'ingrediente', qty: 3, unit: 'g', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-sal' }, block: 'ingrediente', qty: 3, unit: 'g', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-envase' }, block: 'empaque', qty: 1, unit: 'pz', wastePct: 0 },
    ],
    yieldQty: 1,
    yieldUnit: 'pz',
    portions: 1,
    safetyMarginPct: 10,
    useYieldCostAsBase: false,
    lastCostedAt: '2026-05-20',
    active: true,
    updatedAt: now,
  },
  {
    tenantId: T,
    recipeId: 'rec-croquetas',
    name: 'Croquetas caseras (12 ud)',
    kind: 'plato',
    code: 'ENT002',
    group: 'Entrantes',
    allergens: ['gluten', 'lacteos'],
    components: [
      { ref: { kind: 'recipe', recipeId: 'rec-sofrito' }, block: 'ingrediente', qty: 300, unit: 'g', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-harina' }, block: 'ingrediente', qty: 250, unit: 'g', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-nata' }, block: 'ingrediente', qty: 500, unit: 'ml', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-mantequilla' }, block: 'ingrediente', qty: 120, unit: 'g', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-sal' }, block: 'ingrediente', qty: 8, unit: 'g', wastePct: 0 },
      { ref: { kind: 'ingredient', ingredientId: 'ing-envase' }, block: 'empaque', qty: 2, unit: 'pz', wastePct: 0 },
    ],
    yieldQty: 12,
    yieldUnit: 'pz',
    portions: 2,
    safetyMarginPct: 10,
    useYieldCostAsBase: false,
    lastCostedAt: '2026-05-20',
    active: true,
    updatedAt: now,
  },
];

export const demoMenuItems: MenuItem[] = [
  {
    tenantId: T, menuItemId: 'menu-solomillo', recipeId: 'rec-solomillo',
    name: 'Solomillo con pesto y patata', sellingPriceGross: pvp(24.5), vatPct: IVA,
    targetFoodCostPct: 30, theoreticalCost: p(6.1), unitsSold: 320, active: true, updatedAt: now,
    // PVP propio en Uber (cubre la comision del 30 %) para ensenyar la feature.
    channelPrices: { 'plt-uber': pvp(29.9) },
  },
  {
    tenantId: T, menuItemId: 'menu-pulpo', recipeId: 'rec-pulpo',
    name: 'Pulpo a la gallega', sellingPriceGross: pvp(18.9), vatPct: IVA,
    targetFoodCostPct: 30, theoreticalCost: p(4.9), unitsSold: 410, active: true, updatedAt: now,
    // El pulpo no viaja: fuera de las cartas de delivery.
    channelPrices: { 'plt-uber': null, 'plt-glovo': null },
  },
  {
    tenantId: T, menuItemId: 'menu-croquetas', recipeId: 'rec-croquetas',
    name: 'Croquetas caseras (6 ud)', sellingPriceGross: pvp(9.5), vatPct: IVA,
    targetFoodCostPct: 28, theoreticalCost: p(2.2), unitsSold: 690, active: true, updatedAt: now,
  },
  {
    tenantId: T, menuItemId: 'menu-croquetas-uber', recipeId: 'rec-croquetas',
    name: 'Croquetas caseras (6 ud) — Uber Eats', sellingPriceGross: pvp(11.5), vatPct: IVA,
    targetFoodCostPct: 28, platformId: 'plt-uber', unitsSold: 180, active: true, updatedAt: now,
  },
];

/* ------------------------------------------------------------------ */
/* Actividad: un ano de facturacion diaria, mas el mes en curso        */
/* ------------------------------------------------------------------ */

const BASE_MONTHLY_REVENUE = p(48_000);
/** Ticket medio y gasto por mesa, para deducir comensales y mesas del dia. */
const TICKET_MEDIO = p(31.5);
const GASTO_POR_MESA = p(78);

/** Factor por dia de la semana: lunes flojo, viernes y sabado fuertes. */
const WEEKDAY_FACTOR = [0.55, 0.7, 0.85, 1.0, 1.5, 1.75, 0.95]; // lun..dom

function daysOfMonth(month: string): string[] {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

function weekdayIndex(date: string): number {
  const jsDay = new Date(`${date}T00:00:00Z`).getUTCDay();
  return jsDay === 0 ? 6 : jsDay - 1;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

export const demoRevenues: DailyRevenue[] = DEMO_MONTHS.flatMap((month) => {
  const days = daysOfMonth(month);
  // El peso del dia de la semana, con un ±8 % propio de cada fecha: un
  // sabado de lluvia y un sabado de puente no facturan igual. Se normaliza
  // sobre el total, asi que la suma del mes es exactamente la del mes.
  const weights = days.map((d) => WEEKDAY_FACTOR[weekdayIndex(d)] * (1 + 0.08 * ruido(`dia:${d}`)));
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const monthRevenue = BASE_MONTHLY_REVENUE * estacionalidad(month);

  return days.map((date, di) => {
    const dayRevenue = (monthRevenue * weights[di]) / totalWeight;
    const delivery = dayRevenue * 0.14;
    const inHouseTotal = dayRevenue - delivery;

    return {
      tenantId: T,
      date,
      inHouse: {
        efectivo: r2(inHouseTotal * 0.18),
        // Débito (0.27) + crédito (0.51) unificados en tarjeta. Se suman los
        // dos valores ya redondeados para que el total diario no cambie ni un
        // céntimo respecto a los datos de antes de la unificación.
        tarjeta: r2(inHouseTotal * 0.27) + r2(inHouseTotal * 0.51),
        otros: r2(inHouseTotal * 0.04),
      },
      delivery: {
        'plt-uber': r2(delivery * 0.62),
        'plt-glovo': r2(delivery * 0.38),
      },
      customers: Math.max(1, Math.round(dayRevenue / TICKET_MEDIO)),
      tables: Math.max(1, Math.round(dayRevenue / GASTO_POR_MESA)),
      courtesies: r2(dayRevenue * 0.008),
      discounts: r2(dayRevenue * 0.012),
      updatedAt: now,
    } satisfies DailyRevenue;
  });
});

export const demoExpenses: ExpenseEntry[] = DEMO_MONTHS.flatMap((month) => {
  const factor = estacionalidad(month);
  const revenue = BASE_MONTHLY_REVENUE * factor;
  // Los variables que no van con las ventas (luz, agua, gas, reparaciones)
  // oscilan a su aire, ±10 % por mes: la factura de la luz nunca es la misma.
  const v = (euros: number, clave: string) => p(euros) * (1 + 0.1 * ruido(`${clave}:${month}`));
  const rows: Array<[ExpenseCategory, ExpenseEntry['nature'], number, number, ExpenseEntry['paymentType']]> = [
    ['compras_materia_prima', 'variable', revenue * 0.278, IVA, 'credito'],
    ['otros_insumos', 'variable', revenue * 0.035, IVA, 'credito'],
    ['sueldos_salarios', 'fijo', p(13_900), 0, 'contado'],
    ['cuotas_ss', 'fijo', p(4_450), 0, 'contado'],
    ['alquiler', 'fijo', p(3_800), IVA, 'contado'],
    // La luz sube con el verano (terraza, camaras a tope) y con el invierno.
    ['suministros_electricidad', 'variable', v(1_100 * (1 + 0.5 * Math.abs(factor - 1)), 'luz'), IVA, 'contado'],
    ['suministros_agua', 'variable', v(210, 'agua'), IVA, 'contado'],
    ['suministros_gas', 'variable', v(320, 'gas'), IVA, 'contado'],
    ['publicidad_rrss', 'fijo', p(750), IVA, 'contado'],
    ['honorarios', 'fijo', p(420), IVA, 'contado'],
    ['reparaciones', 'variable', v(380, 'reparaciones'), IVA, 'contado'],
    ['seguros', 'fijo', p(210), 0, 'contado'],
    ['comisiones_bancarias', 'variable', revenue * 0.86 * 0.021, 0, 'contado'],
    ['servicios_varios', 'fijo', p(540), IVA, 'contado'],
    ['telefonia', 'fijo', p(95), IVA, 'contado'],
    ['uniformidad', 'fijo', p(120), IVA, 'contado'],
    ['comunicacion_web', 'fijo', p(180), IVA, 'contado'],
    ['papeleria', 'fijo', p(60), IVA, 'contado'],
    ['intereses', 'fijo', p(310), 0, 'contado'],
  ];

  // Subgrupo de demo por categoria, para que el desglose tenga chicha
  const SUBGRUPO: Partial<Record<ExpenseCategory, string>> = {
    compras_materia_prima: 'Carnes y pescados',
    otros_insumos: 'Bodega',
    sueldos_salarios: 'Cocina',
    cuotas_ss: 'Cocina',
    alquiler: 'Local',
    publicidad_rrss: 'Marketing',
    comunicacion_web: 'Marketing',
  };

  const entries: ExpenseEntry[] = rows.map(([category, nature, amount, vatPct, paymentType]) => ({
    tenantId: T,
    entryId: `exp-${month}-${category}`,
    paymentType,
    date: `${month}-${lastDay(month)}`,
    dueDate: paymentType === 'credito' ? `${addMonths(month, 1)}-15` : undefined,
    paymentDate: paymentType === 'credito' ? `${addMonths(month, 1)}-15` : `${month}-${lastDay(month)}`,
    concept: `${category.replace(/_/g, ' ')} ${month}`,
    category,
    subgroup: SUBGRUPO[category],
    nature,
    netAmount: r2(amount),
    vatPct,
    paid: true,
    invoiceStatus: 'facturado',
  }));

  // Comisiones de las plataformas de delivery
  entries.push({
    tenantId: T,
    entryId: `exp-${month}-plataformas`,
    paymentType: 'contado',
    date: `${month}-${lastDay(month)}`,
    paymentDate: `${month}-${lastDay(month)}`,
    concept: `Comisiones de plataformas ${month}`,
    category: 'servicios_varios',
    nature: 'variable',
    netAmount: r2(revenue * 0.14 * 0.28),
    vatPct: IVA,
    paid: true,
    invoiceStatus: 'facturado',
  });

  return entries;
});

// Un par de facturas a credito sin pagar, para las cuentas por pagar. Vencen
// en el mes ANTERIOR al de hoy, no en el de hoy: tienen que estar vencidas
// siempre, se entre el dia 2 o el 28.
demoExpenses.push(
  {
    tenantId: T,
    entryId: 'exp-pendiente-1',
    paymentType: 'credito',
    date: diaDe(-2, '18'),
    dueDate: diaDe(-1, '18'),
    concept: 'Distribuciones del Norte',
    invoiceNumber: 'VB-2231',
    category: 'compras_materia_prima',
    nature: 'variable',
    netAmount: p(1_240),
    vatPct: IVA,
    paid: false,
    invoiceStatus: 'facturado',
  },
  {
    tenantId: T,
    entryId: 'exp-pendiente-2',
    paymentType: 'credito',
    date: diaDe(-2, '02'),
    dueDate: diaDe(-1, '02'),
    concept: 'Bodegas Peña — vinos',
    invoiceNumber: 'BP-8891',
    category: 'otros_insumos',
    nature: 'variable',
    netAmount: p(860),
    vatPct: IVA,
    paid: false,
    invoiceStatus: 'pendiente',
  },
);

export const demoInvestments: Investment[] = [
  {
    tenantId: T, investmentId: 'inv-horno', date: diaDe(-5, '10'),
    concept: 'Horno mixto Rational', quantity: 1, unitPrice: p(18_500), vatPct: IVA,
    usefulLifeYears: 8, fundingSource: 'leasing',
  },
  {
    tenantId: T, investmentId: 'inv-terraza', date: diaDe(-3, '02'),
    concept: 'Mobiliario de terraza', quantity: 12, unitPrice: p(516.67), vatPct: IVA,
    usefulLifeYears: 5, fundingSource: 'propio',
  },
  {
    tenantId: T, investmentId: 'inv-tpv', date: diaDe(-4, '15'),
    concept: 'TPV y software de sala', quantity: 2, unitPrice: p(1_200), vatPct: IVA,
    usefulLifeYears: 3, fundingSource: 'propio',
  },
];

/**
 * Recuentos de inventario de los dos últimos meses de la demo: así la cuenta
 * de resultados enseña el consumo real (iniciales + compras − finales) en el
 * último mes, y solo "compras − finales" en el penúltimo (primer recuento).
 */
export const demoInventoryCounts: InventoryCount[] = [
  DEMO_MONTHS[DEMO_MONTHS.length - 2],
  MES_FINAL,
].map((month, i) => {
    const { lines, totalValue } = valueInventoryLines(
      [
        { ingredientId: 'ing-solomillo', qty: i === 0 ? 14 : 11 },
        { ingredientId: 'ing-pulpo', qty: i === 0 ? 9 : 12 },
        { ingredientId: 'ing-aove', qty: i === 0 ? 26 : 19 },
        { ingredientId: 'ing-harina', qty: i === 0 ? 40 : 32 },
        { ingredientId: 'ing-patata', qty: i === 0 ? 55 : 48 },
        { ingredientId: 'ing-parmesano', qty: i === 0 ? 6 : 4.5 },
        { ingredientId: 'ing-envase', qty: i === 0 ? 320 : 260 },
      ],
      demoIngredients,
    );
    return { tenantId: T, month, lines, totalValue, updatedAt: now };
});

export const demoTargets: Target[] = DEMO_MONTHS.map((month) => ({
  tenantId: T,
  month,
  // El objetivo se fija al empezar el mes, sin conocer el ruido del mes: va
  // sobre la estacionalidad "de libro", y por eso unos meses se cumplen y
  // otros no.
  netRevenue: Math.round(BASE_MONTHLY_REVENUE * ESTACIONALIDAD_ANUAL[Number(month.slice(5, 7)) - 1] * 1.05),
  averageTicket: p(33),
  foodCostPct: 30,
  laborCostPct: 32,
  primeCostPct: 60,
  ebitdaPct: 12,
  safetyMarginPct: 20,
}));

export const demoDataset = {
  settings: demoSettings,
  platforms: demoPlatforms,
  ingredients: demoIngredients,
  recipes: demoRecipes,
  menuItems: demoMenuItems,
  revenues: demoRevenues,
  expenses: demoExpenses,
  investments: demoInvestments,
  targets: demoTargets,
  inventoryCounts: demoInventoryCounts,
  cashMovements: [],
  period: { start: `${DEMO_MONTHS[0]}-01`, end: `${MES_FINAL}-${lastDay(MES_FINAL)}` },
};
